import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { BookCard } from "@/components/BookCard";
import { Compass, Sparkles } from "lucide-react";

const AKIMLAR = [
  "Stoacılık", "Varoluşçuluk", "Antik Felsefe", "Modern Felsefe", "Doğu Felsefesi",
  "Rasyonalizm", "Nihilizm", "Fenomenoloji", "Taoizm", "Etik", "Metafizik", "Siyaset",
];
const TEMALAR = [
  "Psikoloji", "Tarih", "Mitoloji", "Edebiyat", "Bilim", "Şiir", "İslam", "Tasavvuf", "Mistisizm",
];

export default function Explore() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(searchParams.get("etiket") || "");
  const [zorluk, setZorluk] = useState("");
  const [books, setBooks] = useState([]);
  const [popTags, setPopTags] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/books/popular-tags").then((r) => setPopTags(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    if (!active && !zorluk) { setBooks([]); return; }
    setLoading(true);
    const params = { sort: "populer" };
    if (active) params.etiket = active;
    if (zorluk) params.zorluk = zorluk;
    const { data } = await api.get("/books", { params });
    setBooks(data.slice(0, 24));
    setLoading(false);
  }, [active, zorluk]);
  useEffect(() => { load(); }, [load]);

  const pick = (t) => {
    const next = active === t ? "" : t;
    setActive(next);
    setSearchParams(next ? { etiket: next } : {});
  };

  const Chip = ({ t }) => (
    <button key={t} data-testid={`explore-chip-${t}`} onClick={() => pick(t)}
      className={`text-sm px-3.5 py-1.5 rounded-full border transition-colors ${active === t
        ? "bg-terra text-white border-terra"
        : "bg-surface text-ink-2 border-line hover:border-terra hover:text-terra"}`}>
      {t}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="fade-up flex items-center gap-2">
        <Compass className="text-terra" size={22} />
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-terra">Keşfet</p>
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mt-1">Düşünce Evrenini Keşfet</h1>
      <p className="text-ink-2 mt-3 max-w-2xl leading-relaxed">
        Felsefe akımlarına, dönemlere ve temalara göz at. Bir başlığa dokun, o alandaki en popüler kitapları gör.
      </p>

      <div className="mt-8 space-y-5">
        <div>
          <p className="text-sm font-medium text-ink mb-2.5">Felsefe Akımları & Alanlar</p>
          <div className="flex flex-wrap gap-2" data-testid="explore-akimlar">{AKIMLAR.map((t) => <Chip key={t} t={t} />)}</div>
        </div>
        <div>
          <p className="text-sm font-medium text-ink mb-2.5">Temalar</p>
          <div className="flex flex-wrap gap-2" data-testid="explore-temalar">{TEMALAR.map((t) => <Chip key={t} t={t} />)}</div>
        </div>
        <div>
          <p className="text-sm font-medium text-ink mb-2.5">Zorluk Seviyesi</p>
          <div className="flex flex-wrap gap-2" data-testid="explore-zorluk">
            {["Başlangıç", "Orta", "İleri"].map((z) => (
              <button key={z} data-testid={`explore-zor-${z}`} onClick={() => setZorluk(zorluk === z ? "" : z)}
                className={`text-sm px-3.5 py-1.5 rounded-full border transition-colors ${zorluk === z
                  ? "bg-ink text-page border-ink" : "bg-surface text-ink-2 border-line hover:border-ink"}`}>{z}</button>
            ))}
          </div>
        </div>
      </div>

      {(active || zorluk) ? (
        <div className="mt-10">
          <div className="flex items-baseline justify-between">
            <h2 className="font-serif text-2xl text-ink">{active || "Kitaplar"}{zorluk ? ` · ${zorluk}` : ""}</h2>
            {!loading && <span className="text-sm text-muted-foreground">{books.length} kitap</span>}
          </div>
          {loading ? (
            <p className="text-muted-foreground mt-6">Yükleniyor...</p>
          ) : books.length === 0 ? (
            <p className="text-muted-foreground mt-6" data-testid="explore-empty">Bu seçime uygun kitap bulunamadı.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 mt-5" data-testid="explore-results">
              {books.map((b) => <BookCard key={b.id} book={b} />)}
            </div>
          )}
        </div>
      ) : popTags.length > 0 && (
        <div className="mt-10 border border-line rounded-xl p-6 bg-surface" data-testid="explore-popular">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-terra" />
            <p className="text-sm font-medium text-ink">Toplulukta En Çok Okunan Konular</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {popTags.map((t) => (
              <button key={t.etiket} onClick={() => pick(t.etiket)} data-testid={`explore-pop-${t.etiket}`}
                className="text-sm px-3.5 py-1.5 rounded-full border border-terra/20 bg-terra/8 text-terra hover:bg-terra hover:text-white transition-colors">
                {t.etiket} <span className="opacity-60">· {t.sayi}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
